/**
 * @fileoverview Hook de Actions Layer para asignar tópicos/sub-tópicos en ConversationTopicsModalMC.
 * Permite asignar tópicos y sub-tópicos a un cliente desde el modal, con manejo robusto de errores y debug tracking.
 * @module useConversationTopicsModalActions
 * @category Hook - Actions Layer
 * @since 3.1.0
 * @version 3.1.1
 */
/**
 * @typedef {Object} ConversationTopicsModalParams
 * @description Parámetros requeridos para el hook de asignación de tópicos.
 * @property {string|number} customerId - ID del cliente
 * @property {string} platform - Plataforma (whatsapp, messenger, etc)
 * @property {function} toggleModal - Handler para cerrar el modal
 */

/**
 * @typedef {Object} HandleSaveArgs
 * @description Argumentos para la función handleSave.
 * @property {string|number|null} selectedTopic - ID del tópico seleccionado
 * @property {string|number|null} selectedSubtopic - ID del sub-tópico seleccionado
 */

/**
 * Hook Actions Layer para manejar la asignación de tópicos/sub-tópicos a un cliente.
 *
 * Implementa el patrón de **Actions Layer** con separación estricta de responsabilidades:
 * - Solo operaciones de negocio y mutaciones de estado global
 * - Sin lógica de UI ni validación de formularios
 * - Manejo robusto de errores y debug tracking
 *
 * **Arquitectura Optimizada (v3.1.1):**
 * - ✅ Solo mutaciones de negocio (asignar tópico/sub-tópico)
 * - ✅ useCallback para performance
 * - ✅ Manejo de errores y dispatch global
 *
 * @function useConversationTopicsModalActions
 * @param {ConversationTopicsModalParams} params - Parámetros requeridos para el hook
 * @returns {{ handleSave: function }} Objeto con función para guardar la asignación
 *
 * @see {@link ../../../../app/javascript/presentation/modules/inbox/components/ConversationTopicsModalMC.js} Uso en modal de tópicos
 * @see {@link ../../../../app/javascript/presentation/models/CustomersModelMc.js} Modelo de customers
 *
 * @since 3.1.0
 * @version 3.1.1 - Documentación y typedefs agregados
 */
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import customersModelMc from '../../../models/CustomersModelMc';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { isInboxMsChat } from '../../../../services/inbox/inboxMsChatPlatform';

/**
 * Hook Actions Layer para manejar la asignación de tópicos/sub-tópicos.
 *
 * @param {Object} params
 * @param {string|number} params.customerId - ID del cliente
 * @param {string} params.platform - Plataforma (whatsapp, messenger, etc)
 * @param {string|number} params.chatId - ID de la conversación en MongoDB
 * @param {string|number|null} params.selectedTopic - ID del tópico seleccionado
 * @param {string|number|null} params.selectedSubtopic - ID del sub-tópico seleccionado
 * @param {function} params.toggleModal - Handler para cerrar modal
 * @returns {Object} { handleSave }
 */
export default function useConversationTopicsModalActions({
  customerId,
  platform,
  chatId,
  toggleModal
}) {
  const dispatch = useDispatch();

  /**
   * Asigna el tópico/sub-tópico al cliente.
   *
   * @async
   * @function handleSave
   * @memberof useConversationTopicsModalActions
   * @param {HandleSaveArgs} args - Argumentos con IDs de tópico y sub-tópico
   * @returns {Promise<void>} No retorna valor, solo efectos secundarios
   * @throws {Error} Si ocurre un error en la asignación
   *
   * @example
   * await handleSave({ selectedTopic: 1, selectedSubtopic: null });
   */
  const handleSave = useCallback(
    async ({ selectedTopic, selectedSubtopic }) => {
      try {
        const topicable_type = selectedSubtopic ? 'ConversationSubtopic' : 'ConversationTopic';
        const topicable_id = selectedSubtopic ? selectedSubtopic : selectedTopic;

        if (isInboxMsChat(platform)) {
          await customersModelMc.assignMicroserviceCustomerTopic({
            chatId,
            platform,
            topicable_type,
            topicable_id
          });
        } else {
          const body = {
            customerId,
            topicable_type,
            topicable_id,
            channel: platform
          };
          await customersModelMc.assignCustomerTopic(body);
        }
        toggleModal?.();
      } catch (error) {
        const errorMessage = error?.message || error || 'Error assigning conversation topics';
        dispatch({
          type: LOAD_DATA_FAILURE,
          error: errorMessage
        });
      }
    },
    [customerId, platform, chatId, toggleModal, dispatch]
  );

  return { handleSave };
}
